/**
 * Turns a base64 receipt PDF into a browser-native download.
 *
 * The server hands back the PDF as base64 rather than a URL, so there is nothing to navigate to —
 * the bytes are wrapped in a Blob and handed to a synthetic anchor click.
 */

/**
 * Saves a base64-encoded PDF to the user's device under `filename`.
 *
 * @param document Base64-encoded PDF payload, exactly as the server returned it.
 * @param filename Name to save the file under, exactly as the server returned it.
 */
export function downloadReceiptFile(document: string, filename: string): void {
  // `document` is the base64 payload, so reach for the DOM through globalThis to avoid the shadow.
  const dom = globalThis.document;

  const binary = atob(document);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const anchor = dom.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';

  // The anchor has to be in the document for the synthetic click to start a download in Firefox.
  dom.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  // Revoking in the same task can cancel the download before WebKit has read the blob, which
  // matters because both portals also run inside WKWebView. One macrotask is enough.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
